import { InputHTMLAttributes, forwardRef, useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/16/solid';
import { cn } from '@/lib/utils';

export interface CountryOption {
  code: string;
  dialCode: string;
  name?: string;
}

export interface PhoneInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  /** Mensaje de error */
  error?: string;
  /** Label del input */
  label?: string;
  /** Texto de ayuda */
  helpText?: string;
  /** Lista de países disponibles */
  countries?: CountryOption[];
  /** Código de país seleccionado */
  countryCode?: string;
  /** Callback cuando cambia el código de país */
  onCountryChange?: (countryCode: string) => void;
  /** Callback cuando cambia el número */
  onChange?: (value: string) => void;
  /** Valor del número de teléfono */
  value?: string;
}

const DEFAULT_COUNTRIES: CountryOption[] = [
  { code: 'US', dialCode: '+1', name: 'United States' },
  { code: 'CA', dialCode: '+1', name: 'Canada' },
  { code: 'MX', dialCode: '+52', name: 'Mexico' },
  { code: 'AR', dialCode: '+54', name: 'Argentina' },
  { code: 'BR', dialCode: '+55', name: 'Brazil' },
  { code: 'CL', dialCode: '+56', name: 'Chile' },
  { code: 'CO', dialCode: '+57', name: 'Colombia' },
  { code: 'ES', dialCode: '+34', name: 'Spain' },
  { code: 'GB', dialCode: '+44', name: 'United Kingdom' },
  { code: 'DE', dialCode: '+49', name: 'Germany' },
  { code: 'FR', dialCode: '+33', name: 'France' },
  { code: 'IT', dialCode: '+39', name: 'Italy' },
];

/**
 * PhoneInput - Input de teléfono con selector de código de país
 * 
 * @example
 * ```tsx
 * const [phone, setPhone] = useState('');
 * const [country, setCountry] = useState('US');
 * 
 * <PhoneInput
 *   label="Phone number"
 *   value={phone}
 *   onChange={setPhone}
 *   countryCode={country}
 *   onCountryChange={setCountry}
 *   placeholder="123-456-7890"
 * />
 * ```
 */
export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ 
    error, 
    label, 
    helpText,
    countries = DEFAULT_COUNTRIES,
    countryCode: controlledCountryCode,
    onCountryChange,
    onChange,
    value,
    className = '', 
    id, 
    disabled,
    placeholder = '123-456-7890',
    ...props 
  }, ref) => {
    const inputId = id || `phone-${Math.random().toString(36).substring(2, 9)}`;
    const hasError = !!error;
    
    // Estado interno para país si no está controlado
    const [internalCountryCode, setInternalCountryCode] = useState(countries[0]?.code || 'US');
    const selectedCountryCode = controlledCountryCode ?? internalCountryCode;
    
    const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newCode = e.target.value;
      if (onCountryChange) {
        onCountryChange(newCode);
      } else {
        setInternalCountryCode(newCode);
      }
    };
    
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (onChange) {
        onChange(e.target.value);
      }
    };
    
    const describedBy = hasError 
      ? `${inputId}-error` 
      : helpText 
        ? `${inputId}-description` 
        : undefined;

    return (
      <div>
        {label && (
          <label 
            htmlFor={inputId} 
            className="block text-sm/6 font-medium text-secondary-900 dark:text-white"
          >
            {label}
          </label>
        )}
        <div className={cn(label && 'mt-2')}>
          <div 
            className={cn(
              'flex rounded-md bg-white',
              // Normal state (usando border)
              !hasError && [
                'border border-secondary-300',
                'focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500',
              ],
              // Error state
              hasError && [
                'border border-danger-300',
                'focus-within:border-danger-500 focus-within:ring-1 focus-within:ring-danger-500',
              ],
              // Dark mode
              'dark:bg-white/5',
              !hasError && 'dark:border-white/10 dark:focus-within:border-primary-500 dark:focus-within:ring-primary-500',
              hasError && 'dark:border-danger-500/50 dark:focus-within:border-danger-500 dark:focus-within:ring-danger-500',
              // Disabled
              disabled && 'cursor-not-allowed bg-secondary-50 dark:bg-white/[0.02]'
            )}
          >
            {/* Country selector */}
            <div className="grid shrink-0 grid-cols-1 focus-within:relative">
              <select
                id={`${inputId}-country`}
                name="country"
                autoComplete="country"
                aria-label="Country"
                value={selectedCountryCode}
                onChange={handleCountryChange}
                disabled={disabled}
                className={cn(
                  'col-start-1 row-start-1 w-full appearance-none rounded-l-md py-1.5 pr-7 pl-3',
                  'text-base sm:text-sm/6 bg-transparent border-0',
                  'focus:outline-none focus:ring-0',
                  // Normal
                  !hasError && 'text-secondary-500 dark:text-secondary-400',
                  // Error
                  hasError && 'text-danger-500 dark:text-danger-400',
                  // Disabled
                  'disabled:cursor-not-allowed disabled:text-secondary-400'
                )}
              >
                {countries.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.code}
                  </option>
                ))}
              </select>
              <ChevronDownIcon
                aria-hidden="true"
                className={cn(
                  'pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end sm:size-4',
                  hasError 
                    ? 'text-danger-500 dark:text-danger-400' 
                    : 'text-secondary-500 dark:text-secondary-400'
                )}
              />
            </div>
            
            {/* Phone number input */}
            <input
              ref={ref}
              id={inputId}
              type="tel"
              autoComplete="tel"
              placeholder={placeholder}
              value={value}
              onChange={handleInputChange}
              disabled={disabled}
              className={cn(
                'block min-w-0 grow py-1.5 pr-3 pl-1 border-0',
                'text-base sm:text-sm/6 bg-transparent',
                'focus:outline-none focus:ring-0',
                // Normal
                !hasError && [
                  'text-secondary-900 placeholder:text-secondary-400',
                  'dark:text-white dark:placeholder:text-secondary-500',
                ],
                // Error
                hasError && [
                  'text-danger-900 placeholder:text-danger-300',
                  'dark:text-danger-300 dark:placeholder:text-danger-400',
                ],
                // Disabled
                'disabled:cursor-not-allowed disabled:text-secondary-500',
                className
              )}
              aria-invalid={hasError ? 'true' : undefined}
              aria-describedby={describedBy}
              {...props}
            />
          </div>
        </div>
        
        {/* Help text */}
        {helpText && !hasError && (
          <p 
            id={`${inputId}-description`} 
            className="mt-2 text-sm text-secondary-500 dark:text-secondary-400"
          >
            {helpText}
          </p>
        )}
        
        {/* Error message */}
        {hasError && (
          <p 
            id={`${inputId}-error`} 
            className="mt-2 text-sm text-danger-600 dark:text-danger-400" 
            role="alert"
          >
            {error}
          </p>
        )}
      </div>
    );
  }
);

PhoneInput.displayName = 'PhoneInput';
